import {
	FormFields,
	validatorAddress,
	validatorEmail,
	validatorName,
	validatorPhone,
	validatorRequired,
	validatorRequiredUnless,
	validatorRequiredWith,
	validatorSsn,
	validatorZipCode,
} from '@/components/fields/validation';
import { RawLeadData, RequestData } from '@/types/lead';
import { LENDERSOCKET_SERVICE_PROVIDER, UNITED_STATES } from '@/common';

const fields = {
	// personal info
	firstName: {
		value: '',
		validators: ['required', 'name', 'max:15'],
	},
	middleInitial: {
		value: '',
		validators: ['name', 'max:1'],
	},
	lastName: {
		value: '',
		validators: ['required', 'name', 'max:25'],
	},
	suffix: {
		value: '',
		validators: [],
	},
	dateOfBirth: {
		value: undefined,
		validators: ['required', 'aboveAge'],
	},
	ssn: {
		value: '',
		validators: ['required', 'ssn'],
	},
	driverLicenseNumber: {
		value: '',
		validators: ['driverLicenseNumber', 'max:15', 'name'],
	},
	driverLicenseState: {
		value: '',
		validators: ['driverLicenseState'],
	},
	primaryPhoneNumber: {
		value: '',
		validators: ['required', 'phoneNumber'],
	},
	secondaryPhoneNumber: {
		value: '',
		validators: ['phoneNumber'],
	},
	email: {
		value: '',
		validators: ['required', 'email'],
	},
	passportNumber: {
		value: '',
		validators: ['passport', 'max:17'],
	},
	passportExpirationDate: {
		value: undefined,
		validators: ['requiredIfPassport'],
	},
	passportIssuingState: {
		value: '',
		validators: ['requiredIfPassport'],
	},
	education: {
		value: '',
		validators: ['required'],
	},
	// current address
	addressLine1: {
		value: '',
		validators: ['required', 'address', 'max:60', 'currentAddress'],
	},
	addressLine2: {
		value: '',
		validators: ['address', 'max:60'],
	},
	country: {
		value: '',
		validators: ['required', 'currentAddress'],
	},
	city: {
		value: '',
		validators: ['required', 'max:20', 'currentAddress'],
	},
	state: {
		value: '',
		validators: ['required', 'currentAddress'],
	},
	zipCode: {
		value: '',
		validators: ['required', 'zipCode', 'currentAddress'],
	},
	// housing info
	housingStatus: {
		value: '',
		validators: ['required'],
	},
	monthlyMortgageOrRent: {
		value: 0,
		validators: ['required', 'numeric', 'between:0,99999'],
	},
	durationOfResidence: {
		value: '',
		validators: ['required', 'numeric', 'between:1,1199', 'lessThanDob'],
	},
	// previous address
	previousAddressLine1: {
		value: '',
		validators: ['address', 'max:60', 'previousAddress'],
	},
	previousAddressLine2: {
		value: '',
		validators: ['address', 'max:60'],
	},
	previousCountry: {
		value: '',
		validators: ['previousAddress'],
	},
	previousCity: {
		value: '',
		validators: ['max:20', 'previousAddress'],
	},
	previousState: {
		value: '',
		validators: ['previousAddress'],
	},
	previousZipCode: {
		value: '',
		validators: ['zipCode', 'previousAddress'],
	},
	previousDurationOfResidence: {
		value: '',
		validators: ['numeric', 'between:1,1199', 'lessThanDob'],
	},
	// employment info
	employmentStatus: {
		value: '',
		validators: ['required'],
	},
	employerName: {
		value: '',
		validators: ['employerName', 'max:30'],
	},
	durationEmployed: {
		value: '',
		validators: ['numeric', 'between:1,1199'],
	},
	employerPhoneNumber: {
		value: '',
		validators: ['employerPhoneNumber', 'phoneNumber'],
	},
	employerAddressLine1: {
		value: '',
		validators: ['address', 'employerAddress', 'max:60'],
	},
	employerAddressLine2: {
		value: '',
		validators: ['address', 'max:60'],
	},
	employerCity: {
		value: '',
		validators: ['employerAddress', 'max:20'],
	},
	employerState: {
		value: '',
		validators: ['employerAddress'],
	},
	employerZipCode: {
		value: '',
		validators: ['employerAddress', 'zipCode'],
	},
	employerCountry: {
		value: '',
		validators: ['employerAddress'],
	},
	occupationOrJobTitle: {
		value: '',
		validators: ['max:26'],
	},
	// previous employment info
	previousEmploymentStatus: {
		value: '',
		validators: [],
	},
	previousEmployerName: {
		value: '',
		validators: ['previousEmployerName', 'max:30'],
	},
	previousDurationEmployed: {
		value: '',
		validators: ['numeric', 'between:1,1199'],
	},
	previousEmployerPhoneNumber: {
		value: '',
		validators: ['previousEmployerPhoneNumber', 'phoneNumber'],
	},
	previousEmployerAddressLine1: {
		value: '',
		validators: ['address', 'previousEmployerAddress', 'max:60'],
	},
	previousEmployerAddressLine2: {
		value: '',
		validators: ['address', 'max:60'],
	},
	previousEmployerCity: {
		value: '',
		validators: ['previousEmployerAddress', 'max:20'],
	},
	previousEmployerState: {
		value: '',
		validators: ['previousEmployerAddress'],
	},
	previousEmployerZipCode: {
		value: '',
		validators: ['zipCode', 'previousEmployerAddress'],
	},
	previousEmployerCountry: {
		value: '',
		validators: ['previousEmployerAddress'],
	},
	previousOccupationOrJobTitle: {
		value: '',
		validators: ['max:26'],
	},
	// end of employment form
	incomeType: {
		value: '',
		validators: ['requiredWhenEmployment', 'incomeType'],
	},
	incomeAmount: {
		value: '',
		validators: ['requiredWhenEmployment', 'incomeAmount', 'between:1,999999'],
	},
	otherIncomeSource: {
		value: '',
		validators: ['otherIncomeInfo', 'name', 'max:60'],
	},
	otherIncomeType: {
		value: '',
		validators: ['otherIncomeInfo'],
	},
	otherIncomeAmount: {
		value: '',
		validators: ['otherIncomeInfo', 'between:1,999999'],
	},
	// spouse info
	maritalStatus: {
		value: '',
		validators: ['maritalStatus'],
	},
	spouseFirstName: {
		value: '',
		validators: ['whenMarriedAndFromWI', 'spouseName', 'name', 'max:15'],
	},
	spouseMiddleInitial: {
		value: '',
		validators: ['name', 'max:1'],
	},
	spouseLastName: {
		value: '',
		validators: ['whenMarriedAndFromWI', 'spouseName', 'name', 'max:25'],
	},
	spouseAddressLine1: {
		value: '',
		validators: ['whenMarriedAndFromWI', 'address', 'spouseAddress', 'max:60'],
	},
	spouseAddressLine2: {
		value: '',
		validators: ['address', 'max:60'],
	},
	spouseCity: {
		value: '',
		validators: ['whenMarriedAndFromWI', 'spouseAddress', 'max:20'],
	},
	spouseState: {
		value: '',
		validators: ['whenMarriedAndFromWI', 'spouseAddress'],
	},
	spouseZipCode: {
		value: '',
		validators: ['whenMarriedAndFromWI', 'zipCode', 'spouseAddress'],
	},
	spouseCountry: {
		value: '',
		validators: ['whenMarriedAndFromWI', 'spouseAddress'],
	},
	spouseIncomeType: {
		value: '',
		validators: ['spouseIncome'],
	},
	spouseIncomeAmount: {
		value: '',
		validators: ['spouseIncome', 'between:1,999999'],
	},
	spouseOtherIncomeSource: {
		value: '',
		validators: ['spouseOtherIncome', 'name', 'max:60'],
	},
	spouseOtherIncomeType: {
		value: '',
		validators: ['spouseOtherIncome'],
	},
	spouseOtherIncomeAmount: {
		value: '',
		validators: ['spouseOtherIncome', 'between:1,999999'],
	},
	acknowledged: {
		value: undefined,
		validators: ['required'],
	},
	vehicleUsage: {
		value: '',
		validators: ['required'],
	},
};

export const form: FormFields = {
	validated: false,
	fields,
	validators: {
		required: validatorRequired(),
		name: validatorName(),
		phoneNumber: validatorPhone(),
		email: validatorEmail(),
		address: validatorAddress(),
		ssn: validatorSsn(),
		zipCode: (value: string) => validatorZipCode('', UNITED_STATES, value),
		passport: (value: string) => {
			if ((fields.passportIssuingState.value || fields.passportExpirationDate.value) && !value) {
				return 'This field is required';
			}
			return false;
		},
		driverLicenseNumber: (value: string) => validatorRequiredWith(
			'', fields.driverLicenseState.value, value
		),
		driverLicenseState: (value: string) => validatorRequiredWith(
			'', fields.driverLicenseNumber.value, value
		),
		requiredIfPassport: (value: string) => validatorRequiredWith(
			'', fields.passportNumber.value, value
		),
		currentAddress: (value: string) => {
			if (fields.addressLine2.value && !value) {
				return 'This field is required';
			}
			return false;
		},
		previousAddress: (value: string) => {
			if (
				(
					fields.previousAddressLine1.value
					|| fields.previousCountry.value
					|| fields.previousState.value
					|| fields.previousCity.value
					|| fields.previousZipCode.value
				) && !value) {
				return 'This field is required';
			}

			if (fields.previousAddressLine2.value && !value) {
				return 'This field is required';
			}

			return false;
		},
		employerName: (value: string) => validatorRequiredUnless(
			'', fields.employmentStatus.value, 'Retired,Unemployed,Self Employed', value
		),
		durationEmployed: (value: string) => validatorRequiredUnless(
			'', fields.employmentStatus.value, 'Retired,Unemployed,Student', value
		),
		durationEmployedForR1: (value: string) => {
			const employmentStatuses = ['Full Time', 'Military', 'Other', 'Part Time', 'Self Employed', 'Retired'];

			if ((fields.employmentStatus.value && employmentStatuses.indexOf(fields.employmentStatus.value) !== -1) && !value) {
				return 'This field is required';
			}
			return false;
		},
		employerPhoneNumber: (value: string) => validatorRequiredUnless(
			'', fields.employmentStatus.value, 'Retired,Unemployed', value
		),
		employerAddress: (value: string) => {
			if (
				(
					fields.employerAddressLine1.value !== ''
					|| fields.employerCountry.value !== ''
					|| fields.employerCity.value !== ''
					|| fields.employerState.value !== ''
					|| fields.employerZipCode.value !== ''
				)
				&& !value
			) {
				return 'This field is required';
			}

			if (fields.employerAddressLine2.value && !value) {
				return 'This field is required';
			}

			return false;
		},
		previousEmployerName: (value: string) => validatorRequiredUnless(
			'', fields.previousEmploymentStatus.value, 'Retired,Unemployed,Self Employed,', value
		),
		previousDurationEmployed: (value: string) => validatorRequiredUnless(
			'', fields.previousEmploymentStatus.value, 'Retired,Unemployed,Student,', value
		),
		previousDurationEmployedForR1: (value: string) => {
			const employmentStatuses = ['Full Time', 'Military', 'Other', 'Part Time', 'Self Employed', 'Retired'];

			if ((fields.previousEmploymentStatus.value && employmentStatuses.indexOf(fields.previousEmploymentStatus.value) !== -1) && !value) {
				return 'This field is required';
			}
			return false;
		},
		previousEmployerPhoneNumber: (value: string) => validatorRequiredUnless(
			'', fields.previousEmploymentStatus.value, 'Retired,Unemployed', value
		),
		previousEmployerAddress: (value: string) => {
			if (
				(
					fields.previousEmployerAddressLine1.value !== ''
					|| fields.previousEmployerCountry.value !== ''
					|| fields.previousEmployerCity.value !== ''
					|| fields.previousEmployerState.value !== ''
					|| fields.previousEmployerZipCode.value !== ''
				)
				&& !value
			) {
				return 'This field is required';
			}

			if (fields.previousEmployerAddressLine2.value && !value) {
				return 'This field is required';
			}

			return false;
		},
		requiredWhenEmployment: (value: string) => {
			const employmentStatuses = ['Full Time', 'Part Time', 'Retired', 'Military', 'Self Employed'];

			if ((fields.employmentStatus.value && employmentStatuses.indexOf(fields.employmentStatus.value) !== -1) && !value) {
				return 'This field is required';
			}
			return false;
		},
		requiredWhenOccupation: (value: string) => {
			const employmentStatuses = ['Full Time', 'Part Time', 'Other', 'Military', 'Self Employed'];

			if ((fields.employmentStatus.value && employmentStatuses.indexOf(fields.employmentStatus.value) !== -1) && !value) {
				return 'This field is required';
			}
			return false;
		},
		requiredWhenPreviousOccupation: (value: string) => {
			const employmentStatuses = ['Full Time', 'Part Time', 'Other', 'Military', 'Self Employed'];

			if ((fields.previousEmploymentStatus.value && employmentStatuses.indexOf(fields.previousEmploymentStatus.value) !== -1) && !value) {
				return 'This field is required';
			}
			return false;
		},
		incomeType: (value: string) => {
			if (fields.incomeAmount.value && !value) {
				return 'This field is required';
			}
			return false;
		},
		incomeAmount: (value: string) => {
			if (fields.incomeType.value && !value) {
				return 'This field is required';
			}
			return false;
		},
		otherIncomeInfo: (value: string) => {
			if (
				(
					fields.otherIncomeSource.value !== ''
					|| fields.otherIncomeType.value !== ''
					|| fields.otherIncomeAmount.value !== ''
				)
				&& !value
			) {
				return 'This field is required';
			}
			return false;
		},
		maritalStatus: (value: string) => {
			if (fields.state.value === 'WI' && !value) {
				return 'This field is required';
			}
			return false;
		},
		whenMarriedAndFromWI: (value: string) => {
			if (fields.state.value === 'WI' && fields.maritalStatus.value === 'Married' && !value) {
				return 'This field is required';
			}
			return false;
		},
		spouseAddress: (value: string) => {
			if (
				(
					fields.spouseAddressLine1.value !== ''
					|| fields.spouseCountry.value !== ''
					|| fields.spouseCity.value !== ''
					|| fields.spouseState.value !== ''
					|| fields.spouseZipCode.value !== ''
				)
				&& !value
			) {
				return 'This field is required';
			}

			if (fields.spouseAddressLine2.value && !value) {
				return 'This field is required';
			}

			if ((fields.spouseFirstName.value || fields.spouseLastName.value) && !value) {
				return 'This field is required';
			}

			return false;
		},
		spouseIncome: (value: string) => {
			if (fields.spouseIncomeType.value && !value) {
				return 'This field is required';
			}
			if (fields.spouseIncomeAmount.value && !value) {
				return 'This field is required';
			}

			return false;
		},
		spouseOtherIncome: (value: string) => {
			if (
				(
					fields.spouseOtherIncomeType.value !== ''
					|| fields.spouseOtherIncomeSource.value !== ''
					|| fields.spouseOtherIncomeAmount.value !== ''
				)
				&& !value
			) {
				return 'This field is required';
			}
			return false;
		},
		aboveAge: (value: string) => {
			const today = new Date();
			const birthDate = new Date(value);
			let age = today.getFullYear() - birthDate.getFullYear();
			const m = today.getMonth() - birthDate.getMonth();

			if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
				age -= 1;
			}
			if (age < 18) {
				return 'You must be 18+ years old';
			}

			return false;
		},
		lessThanDob: (value: string) => {
			const today = new Date();
			const birthDate = new Date(String(fields.dateOfBirth.value));
			let age = today.getFullYear() - birthDate.getFullYear();
			const m = today.getMonth() - birthDate.getMonth();

			if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
				age -= 1;
			}
			if ((age * 12) < Number(value)) {
				return 'Duration cannot be greater than age.';
			}
			return false;
		},
		spouseName: (value: string) => {
			if ((fields.spouseFirstName.value || fields.spouseLastName.value) && !value) {
				return 'This field is required';
			}
			if (fields.spouseMiddleInitial.value && !(fields.spouseFirstName.value || fields.spouseLastName.value)) {
				return 'This field is required';
			}
			return false;
		},
	},
};

// TODO: We can use library in future to format dates Eg: moment.js
export function formatDate(date: string) {
	if (!date) {
		return '';
	}
	const dateObject = new Date(date);

	const finalDate = `0${dateObject.getDate()}`.slice(-2);

	const finalMonth = `0${(dateObject.getMonth() + 1)}`.slice(-2);

	return `${dateObject.getFullYear()}-${finalMonth}-${finalDate}`;
}

function getCountry(value: string|undefined) {
	if (value !== '') {
		return 'US';
	}

	return '';
}

export function formatCreditAppData(formData: RequestData, data: RawLeadData) {
	const { tradeIn } = data.writeup;

	let leadData: {[id: string]: unknown} = {
		firstname: formData?.firstName,
		middleinitial: formData?.middleInitial,
		lastname: formData?.lastName,
		suffix: formData?.suffix,
		dateofbirth: formatDate(formData?.dateOfBirth as string),
		ssn: formData?.ssn,
		drivinglicencenumber: formData?.driverLicenseNumber,
		drivinglicencestate: formData?.driverLicenseState,
		primaryphone: formData?.primaryPhoneNumber,
		secondaryphone: formData?.secondaryPhoneNumber,
		email: formData?.email,
		'passport-number': formData?.passportNumber,
		'passport-expiration-date': formatDate(formData?.passportExpirationDate as string),
		'passport-issuing-state': formData?.passportIssuingState,
		education: formData?.education,
		'currentaddress-addressline1': formData.addressLine1,
		'currentaddress-addressline2': formData?.addressLine2,
		'currentaddress-country': 'US',
		'currentaddress-state': formData?.state,
		'currentaddress-city': formData?.city,
		'currentaddress-zipcode': formData?.zipCode,
		housingstatus: formData?.housingStatus,
		mortgageorrent: formData?.monthlyMortgageOrRent ?? 0,
		totalmonthsatadddress: formData?.durationOfResidence,
		mortgageorrentcurrency: 'USD',
		'previousaddress-addressline1': formData?.previousAddressLine1,
		'previousaddress-addressline2': formData?.previousAddressLine2,
		'previousaddress-country': getCountry(formData?.previousAddressLine1),
		'previousaddress-state': formData?.previousState,
		'previousaddress-city': formData?.previousCity,
		'previousaddress-zipcode': formData?.previousZipCode,
		'previousaddress-duration': formData?.previousDurationOfResidence,
		'currentemploymentinfo-employmentstatus': formData?.employmentStatus,
		'currentemploymentinfo-employedby': formData?.employerName,
		'currentemploymentinfo-totalmonthsemployed': formData?.durationEmployed,
		'currentemploymentinfo-businessphone': formData?.employerPhoneNumber,
		'currentemploymentinfo-employeraddress-addressline1': formData?.employerAddressLine1,
		'currentemploymentinfo-employeraddress-addressline2': formData?.employerAddressLine2,
		'currentemploymentinfo-employeraddress-country': getCountry(formData?.employerAddressLine1),
		'currentemploymentinfo-employeraddress-state': formData?.employerState,
		'currentemploymentinfo-employeraddress-city': formData?.employerCity,
		'currentemploymentinfo-employeraddress-zipcode': formData?.employerZipCode,
		'currentemploymentinfo-occupation': formData?.occupationOrJobTitle,
		'previousemploymentinfo-employmentstatus': formData?.previousEmploymentStatus,
		'previousemploymentinfo-employedby': formData?.previousEmployerName,
		'previousemploymentinfo-totalmonthsemployed': formData?.previousDurationEmployed,
		'previousemploymentinfo-businessphone': formData?.previousEmployerPhoneNumber,
		'previousemploymentinfo-employeraddress-addressline1': formData?.previousEmployerAddressLine1,
		'previousemploymentinfo-employeraddress-addressline2': formData?.previousEmployerAddressLine2,
		'previousemploymentinfo-employeraddress-country': getCountry(formData?.previousEmployerAddressLine1),
		'previousemploymentinfo-employeraddress-state': formData?.previousEmployerState,
		'previousemploymentinfo-employeraddress-city': formData?.previousEmployerCity,
		'previousemploymentinfo-employeraddress-zipcode': formData?.previousEmployerZipCode,
		'previousemploymentinfo-occupation': formData?.previousOccupationOrJobTitle,
		'income-type': formData?.incomeType,
		'income-value': formData?.incomeAmount ?? 0,
		'income-value-currency': 'USD',
		'other-income-source': formData?.otherIncomeSource,
		'other-income-type': formData?.otherIncomeType,
		'other-income-amount': formData?.otherIncomeAmount ?? 0,
		'other-income-currency': formData?.otherIncomeAmount ? 'USD' : '',
		'marital-status': formData?.maritalStatus,
		'tire-fee': formData?.tireFee,
		'registration-fee': formData?.registrationFee,
		'license-fee': formData?.licenseFee,
		'inspection-fee': formData?.inspectionFee,
		'transfer-plates-fee': formData?.transferPlatesFee,
		'dmv-fee': formData?.dmvFee,
		'doc-fee': formData?.docFee,
		'prep-fee': formData?.prepFee,
		'net-trade-value': tradeIn.tradeinValue ?? 0,
		'estimated-value': tradeIn.amount ?? 0,
		'currently-owed': tradeIn.payoff ?? 0,
		'sales-tax': formData.salesTax ?? 0,
		'finance-method': formData?.dealType === 'loan' ? 'Retail' : 'Lease',
	};

	if (data.lenderSocketServiceProvider === LENDERSOCKET_SERVICE_PROVIDER.routeOne) {
		leadData = {
			...leadData,
			'vehicle-usage': formData?.vehicleUsage,
			'residual-percentage': data.routeOneFieldsData.residualPercentage,
			'estimated-payment': data.routeOneFieldsData.estimatedPayment,
			'financed-amount': data.routeOneFieldsData.financedAmount || 0,
			rebate: data.routeOneFieldsData.rebate,
			apr: data.routeOneFieldsData.apr,
		};

		if (leadData['finance-method'] === 'Lease') {
			leadData = {
				...leadData,
				'base-payment': data.routeOneFieldsData.basePayment,
				'lease-rate': data.routeOneFieldsData.leaseRate,
				'lease-rate-money-factor': data.routeOneFieldsData.leaseRateMoneyFactor,
			};
		}
	} else {
		leadData = {
			...leadData,
			'spouse-first-name': formData?.spouseFirstName,
			'spouse-middle-initial': formData?.spouseMiddleInitial,
			'spouse-last-name': formData?.spouseLastName,
			'spouse-addressline1': formData?.spouseAddressLine1,
			'spouse-addressline2': formData?.spouseAddressLine2,
			'spouse-country': getCountry(formData?.spouseAddressLine1),
			'spouse-state': formData?.spouseState,
			'spouse-city': formData?.spouseCity,
			'spouse-zipcode': formData?.spouseZipCode,
			'spouse-income-type': formData?.spouseIncomeType,
			'spouse-income-amount': formData?.spouseIncomeAmount ?? 0,
			'spouse-other-income-source': formData?.spouseOtherIncomeSource,
			'spouse-other-income-type': formData?.spouseOtherIncomeType,
			'spouse-other-income-amount': formData?.spouseOtherIncomeAmount,
		};
	}

	if (leadData['finance-method'] === 'Lease') {
		leadData['estimated-payment'] = formData?.monthly;
	}

	return leadData;
}
